import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, Search, UserPlus, Users } from 'lucide-react';
import { patientsService } from '@/services/patients';
import { patientIdentifier } from '@/lib/patient';
import { PageHeader, EmptyState, LoadingState } from '@/components/ui';

export function PatientList() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const query = search.trim();
  const canSearch = query.length >= 2;

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['patients', 'search', query],
    queryFn: () => patientsService.search({ q: query, limit: 20 }),
    enabled: canSearch,
  });

  const patients = data?.patients ?? [];

  return (
    <div>
      <PageHeader
        title={t('nav.patients')}
        description={t('doctor.patients.description')}
        actions={
          <Link to="/doctor/patients/new" className="btn-primary">
            <UserPlus className="h-4 w-4" />
            {t('patient.new')}
          </Link>
        }
      />

      <div className="card mb-6 p-5">
        <label htmlFor="patient-search" className="label">
          {t('doctor.patients.searchLabel')}
        </label>
        <div className="relative">
          <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            id="patient-search"
            type="search"
            autoComplete="off"
            placeholder={t('doctor.patients.searchPlaceholder')}
            className="input ps-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <p className="field-hint">{t('doctor.patients.searchHint')}</p>
      </div>

      {!canSearch ? (
        <div className="card p-0">
          <EmptyState
            icon={Search}
            title={t('doctor.patients.startTyping')}
            description={t('doctor.patients.searchHint')}
          />
        </div>
      ) : isLoading ? (
        <div className="card p-0">
          <LoadingState />
        </div>
      ) : isError ? (
        <div className="alert-danger items-center justify-between">
          <span>{t('doctor.loadError')}</span>
          <button type="button" className="btn-secondary btn-sm" onClick={() => refetch()}>
            {t('doctor.retry')}
          </button>
        </div>
      ) : patients.length > 0 ? (
        <div className="card divide-y divide-gray-100 overflow-hidden p-0">
          {patients.map((patient) => {
            const pid = patientIdentifier(patient);
            return (
              <Link
                key={patient.id}
                to={`/doctor/patients/${patient.id}`}
                className="group flex items-center gap-4 px-5 py-4 transition-colors hover:bg-gray-50"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-50 text-sm font-semibold uppercase text-primary-700">
                  {(patient.lastName?.[0] ?? '') + (patient.firstName?.[0] ?? '')}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-gray-900">
                    {patient.lastName} {patient.firstName}
                  </p>
                  <p className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-sm text-gray-500">
                    <span>
                      {t(pid.typeKey)}: <span className="font-mono">{pid.masked}</span>
                    </span>
                    <span>{t('doctor.patients.bornIn', { year: patient.birthYear })}</span>
                    <span>{t(`patient.${patient.gender.toLowerCase()}`)}</span>
                    {patient.phoneLast4 && (
                      <span>{t('doctor.patients.phoneMasked', { last4: patient.phoneLast4 })}</span>
                    )}
                  </p>
                </div>
                <span className="hidden shrink-0 items-center gap-1 text-sm font-medium text-primary-700 sm:inline-flex">
                  {t('doctor.patients.viewRecord')}
                  <ChevronRight className="h-4 w-4 rtl-flip" />
                </span>
                <ChevronRight className="h-5 w-5 shrink-0 text-gray-400 sm:hidden rtl-flip" />
              </Link>
            );
          })}
        </div>
      ) : (
        <div className="card p-0">
          <EmptyState
            icon={Users}
            title={t('common.noResults')}
            description={t('doctor.patients.noMatch', { q: query })}
            action={
              <Link to="/doctor/patients/new" className="btn-primary btn-sm">
                <UserPlus className="h-4 w-4" />
                {t('patient.new')}
              </Link>
            }
          />
        </div>
      )}
    </div>
  );
}
